import styled, {css, keyframes} from 'styled-components';
import {Button, ButtonIcon, Tag} from 'pepsico-ds';

// PepsiCo "Peacock" palette — see src/pages/ChatGenie/Chat/chart/theme.ts.
// Reused here as the accent system for the /raas2 experience.
export const peacock = {
  deepest: '#003B5C',
  navy: '#0E4D7B',
  primary: '#005A8B',
  bright: '#00A0DC',
  teal: '#28B3D8',
  aqua: '#46C2CA',
  aquaLight: '#7AD3DE',
  gold: '#F5B400',
};

export const peacockGradient = `linear-gradient(135deg, ${peacock.primary} 0%, ${peacock.bright} 55%, ${peacock.aqua} 100%)`;

export const PanelWrapper = styled.aside<{$isFullScreen: boolean}>`
  position: relative;
  z-index: 30;
  display: flex;
  flex-direction: column;
  flex: 1;
  /* This component is rendered as a standalone full-page route
     (/raas2) with no ancestor that provides an explicit height, so
     a percentage height would collapse to the content's natural size and
     push the sticky footer up near the top instead of the viewport bottom.
     Anchor to the viewport height directly (falls back to vh for browsers
     without dvh support) so the footer always sits at the bottom. */
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  width: 100%;
  background:
    radial-gradient(
      1100px circle at 50% -8%,
      rgba(0, 160, 220, 0.1),
      transparent 55%
    ),
    radial-gradient(
      900px circle at 100% 100%,
      rgba(70, 194, 202, 0.08),
      transparent 60%
    ),
    #fafcfe;
  border-left: ${({$isFullScreen}) =>
    $isFullScreen ? 'none' : `1px solid rgba(0, 90, 139, 0.16)`};
  box-shadow: ${({$isFullScreen}) =>
    $isFullScreen ? 'none' : '0 2px 24px rgba(0, 59, 92, 0.12)'};

  @media (min-resolution: 1.44dppx) and (max-width: 1440px) {
    box-shadow: 0 2px 18px rgba(0, 94, 192, 0.1);

    .accordion-item-v2 .accordion-header,
    .accordion-item-v2 .header,
    .tab,
    .tab button,
    .tabs-navigation button,
    .tabs-navigation .tab-label,
    .button .label,
    .button .label-text,
    .tag .label,
    .tag .label-text,
    .footer .field input,
    .footer .field textarea,
    .footer .field input::placeholder,
    .footer .field textarea::placeholder {
      font-size: 12px !important;
      line-height: 18px !important;
    }
  }

  @media (max-width: 1024px) {
    box-shadow: 0 2px 18px rgba(0, 94, 192, 0.1);
  }

  @media (max-width: 768px) {
    border-left: none;
    border-top: 1px solid rgba(0, 90, 139, 0.16);
    border-radius: 8px;
  }
`;

export const StickyBar = styled.div<{$isFullScreen: boolean}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(0.5rem, 1.2vw, 1rem);
  min-height: clamp(2.75rem, 4.5vh, 3.5rem);
  padding: clamp(0.625rem, 1vh, 0.875rem) clamp(1rem, 2vw, 1.75rem);
  background: transparent;
  border-bottom: 1px solid rgba(0, 90, 139, 0.1);
  flex-shrink: 0;

  @media (max-width: 768px) {
    min-height: 2.5rem;
    gap: clamp(0.375rem, 1.5vw, 0.5rem);
    flex-wrap: nowrap;
    align-items: center;
  }
`;

export const BrandMark = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
`;

export const BrandIcon = styled.div`
  width: 1.75rem;
  height: 1.75rem;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${peacockGradient};
  box-shadow: 0 2px 10px rgba(0, 90, 139, 0.3);

  .icon,
  svg,
  path {
    color: #ffffff !important;
    fill: #ffffff !important;
  }
`;

export const BrandName = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.875rem, 0.3vw + 0.8rem, 1rem);
  font-weight: 700;
  color: ${peacock.deepest};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const StickyBarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 1448px;
  gap: 16px;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 8px;
    align-items: center;
    flex-wrap: nowrap;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: clamp(0.5rem, 0.9vw, 0.75rem);
  flex-shrink: 0;

  .tooltip-parent {
    display: unset;
  }

  & > button,
  & .base-button {
    height: clamp(1.75rem, 3.2vh, 2.125rem) !important;
    min-height: clamp(1.75rem, 3.2vh, 2.125rem) !important;
    padding: 0 clamp(0.625rem, 1vw, 0.875rem) !important;
    font-size: clamp(0.75rem, 1.4vh, 0.8125rem) !important;
    font-weight: 600 !important;
    border-radius: 999px !important;
    border-color: rgba(0, 90, 139, 0.25) !important;
    color: ${peacock.primary} !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    gap: clamp(0.25rem, 0.6vw, 0.375rem) !important;
  }

  & > button:hover,
  & .base-button:hover {
    background: rgba(0, 90, 139, 0.06) !important;
    border-color: ${peacock.primary} !important;
  }

  & > button .icon,
  & > button svg,
  & .base-button .icon,
  & .base-button svg {
    color: ${peacock.primary} !important;
    width: 1rem !important;
    height: 1rem !important;
    font-size: 1rem !important;
  }

  @media (max-width: 768px) {
    gap: clamp(0.375rem, 1.2vw, 0.5rem);

    & > button .label,
    & .base-button .label {
      display: none;
    }
  }
`;

export const MemoryToggleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;

  .toggle .label,
  .toggle label:not(.switch) {
    font-family: 'Inter', sans-serif;
    font-size: clamp(0.75rem, 1.4vh, 0.8125rem) !important;
    font-weight: 600 !important;
    color: ${peacock.primary} !important;
  }

  @media (max-width: 768px) {
    .toggle .label,
    .toggle label:not(.switch) {
      display: none;
    }
  }
`;

export const HeaderDivider = styled.div`
  width: 1px;
  height: clamp(1rem, 1.8vh, 1.25rem);
  background: rgba(0, 90, 139, 0.18);
  flex-shrink: 0;
  align-self: center;
`;

export const TokenModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const ScrollContent = styled.div<{$isFullScreen: boolean}>`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({$isFullScreen}) => ($isFullScreen ? '14px' : '24px')};
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${({$isFullScreen}) =>
    $isFullScreen
      ? 'clamp(16px, 1.8vh, 24px) clamp(24px, 4vw, 64px) 8px'
      : '24px 24px 8px'};
  background: transparent;
  justify-content: space-between;
  align-items: center;
  /* Fullscreen: span the entire available width. Side-panel keeps default. */
  max-width: 100%;
  margin: 0 auto;
  width: 100%;

  @media (min-resolution: 1.44dppx) and (max-width: 1440px) {
    padding: 16px 16px 8px;
    gap: 16px;
  }

  @media (max-width: 1024px) {
    padding: 16px 16px 8px;
    gap: 16px;
  }

  @media (max-width: 768px) {
    padding: 12px 12px 8px;
    gap: 12px;
  }
`;

export const ScrollTopContent = styled.div<{$isFullScreen: boolean}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  gap: ${({$isFullScreen}) => ($isFullScreen ? '14px' : '24px')};

  @media (min-resolution: 1.44dppx) and (max-width: 1440px) {
    gap: 16px;
  }

  @media (max-width: 1024px) {
    gap: 16px;
  }

  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const orbPulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

export const GreetingSection = styled.section<{$isFullScreen: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: clamp(0.75rem, 1.4vh, 1.25rem);
  width: 100%;
  max-width: 736px;
  text-align: center;
`;

export const GreetingIcon = styled.div`
  /* A soft glowing orb — the page's one signature visual flourish. */
  width: clamp(2.75rem, 3vw + 2rem, 4rem);
  height: clamp(2.75rem, 3vw + 2rem, 4rem);
  border-radius: 50%;
  position: relative;
  background: radial-gradient(
    circle at 32% 28%,
    #d9f4f7 0%,
    ${peacock.aquaLight} 22%,
    ${peacock.bright} 55%,
    ${peacock.primary} 82%,
    ${peacock.deepest} 100%
  );
  box-shadow:
    0 0 0 8px rgba(0, 160, 220, 0.08),
    0 10px 30px rgba(0, 59, 92, 0.28);
  animation: ${orbPulse} 4.5s ease-in-out infinite;

  &::after {
    content: '';
    position: absolute;
    top: 14%;
    left: 20%;
    width: 34%;
    height: 24%;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.75);
    filter: blur(2px);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const GreetingText = styled.p<{$isFullScreen: boolean}>`
  font-family: 'Inter', sans-serif;
  /* Fluid: ~22px on phones → ~34px on desktop. */
  font-size: clamp(1.375rem, 1.4vw + 0.75rem, 2.125rem);
  font-weight: 700;
  line-height: 1.25;
  color: ${peacock.deepest};
  margin: 0;
  text-align: center;
  max-width: 640px;

  .greeting-highlight {
    background: ${peacockGradient};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

export const InsightSection = styled.section<{$isFullScreen: boolean}>`
  display: flex;
  flex-direction: column;
  width: 100%;
  /* Side-panel: fill the column. Fullscreen: scale fluidly with viewport so
     the insights span the same width as the chat content. */
  max-width: ${({$isFullScreen}) =>
    $isFullScreen ? 'min(100%, clamp(40rem, 95vw, 90rem))' : '100%'};
  gap: ${({$isFullScreen}) => ($isFullScreen ? '12px' : '0')};
  padding: 0;
  background: ${({$isFullScreen}) =>
    $isFullScreen ? '#ffffff' : 'transparent'};
  border-radius: ${({$isFullScreen}) => ($isFullScreen ? '8px' : '0')};
  border: none;
  box-shadow: none;

  @media (min-resolution: 1.44dppx) and (max-width: 1440px) {
    gap: 10px;
    padding: 12px;
  }

  @media (max-width: 1024px) {
    gap: 10px;
    padding: 12px;
  }

  @media (max-width: 768px) {
    gap: 10px;
    padding: 10px;
  }

  .accordion-item-v1.border,
  .accordion-item-v2 {
    background-color: transparent;
    border: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    border-radius: 6px;
  }

  @media (min-resolution: 1.44dppx) and (max-width: 1440px) and (min-width: 1024px) {
    .accordion-item-v2 .accordion-header,
    .accordion-item-v2 .header,
    [class*='accordion'] [class*='header'],
    .$tabs-navigation .$tab,
    [class*='tabs-navigation'] [class*='tab'] {
      font-size: 11px !important;
      line-height: 16px !important;
    }

    .$tabs-navigation .$tab,
    [class*='tabs-navigation'] [class*='tab'] {
      height: 28px;
      padding-top: 2px;
      padding-bottom: 2px;
    }
  }
`;

export const StyledAccordion = styled.div<{$isFullScreen: boolean}>`
  width: 100%;
  height: 75%;
  max-height: 75%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  /* Accordion header and content styling as before */
  .accordion-item-v1 {
    border: none;
    background: none;
    box-shadow: none;
  }
  & .accordion-v2 {
    background: transparent;
  }
  & .accordion-item-v1,
  & .accordion-item-v2 {
    border: none;
  }
  & .accordion-item-v1 .buttonBase,
  & .accordion-item-v2 .accordion-header,
  & .accordion-item-v2 .header,
  & .accordion-item-v1 .accordion-item-container {
    padding-left: clamp(0.625rem, 1.4vw, 1.25rem) !important;
    padding-right: clamp(0.625rem, 1.4vw, 1.25rem) !important;
    font-family: 'Inter', sans-serif;
    font-size: clamp(0.75rem, 1.3vh, 0.9375rem) !important;
    font-weight: 600;
    line-height: 1.2 !important;
    color: #2d2d2d;
    min-height: clamp(2.25rem, 4.4vh, 3rem) !important;
    height: clamp(2.25rem, 4.4vh, 3rem) !important;
    display: flex;
    align-items: center;
  }
  & .accordion-item-v1.no-background .buttonBase,
  & .accordion-item-v2 .accordion-header {
    border-bottom: none;
    border-radius: 6px 6px 0 0;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  & .accordion-item-v1 .buttonBase svg,
  & .accordion-item-v1 .buttonBase img,
  & .accordion-item-v2 .accordion-header svg,
  & .accordion-item-v2 .accordion-header img,
  & .accordion-item-v2 .header svg,
  & .accordion-item-v2 .header img {
    width: clamp(0.875rem, 1.9vh, 1.25rem) !important;
    height: clamp(0.875rem, 1.9vh, 1.25rem) !important;
  }
  @media (max-width: 1024px) {
    & .accordion-item-v1 .buttonBase,
    & .accordion-item-v2 .accordion-header,
    & .accordion-item-v2 .header,
    & .accordion-item-v1 .accordion-item-container {
      min-height: clamp(2.125rem, 4vh, 2.75rem) !important;
      height: clamp(2.125rem, 4vh, 2.75rem) !important;
    }
  }
  @media (max-width: 768px) {
    & .accordion-item-v1 .buttonBase,
    & .accordion-item-v2 .accordion-header,
    & .accordion-item-v2 .header,
    & .accordion-item-v1 .accordion-item-container {
      min-height: 2rem !important;
      height: 2rem !important;
      font-size: 0.75rem !important;
    }
  }
  & .accordion-item-v1.no-background[aria-expanded='true'] .content-expander {
    clip-path: none;
    border-top: 1px solid #e8e8e8;
  }
  & .accordion-item-v1 .content-expander,
  & .accordion-item-v2 .accordion-content {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    width: 100% !important;
    max-height: 100% !important;
  }

  &
    .accordion-item-v1.no-background[aria-expanded='true']
    .content-expander
    .accordion-item-content,
  & .accordion-item-v2 .accordion-content {
    padding-left: 12px !important;
    padding-right: 12px !important;
    padding-top: 12px !important;
    padding-bottom: 12px !important;
  }
`;

export const FilterRow = styled.div<{$isFullScreen: boolean}>`
  display: flex;
  flex-wrap: wrap;
  gap: ${({$isFullScreen}) => ($isFullScreen ? '10px' : '8px')};
  width: 100%;
  margin-top: 0;
  align-items: center;

  & > * {
    flex: 0 0 auto;
    max-width: 100%;
  }

  @media (min-resolution: 1.44dppx) and (max-width: 1440px) {
    gap: 10px;
  }

  @media (max-width: 1024px) {
    gap: 10px;
  }

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

export const DescriptionText = styled.p<{$isFullScreen: boolean}>`
  margin: 0;
  color: #4b4b4b;
  font-family: 'Inter', sans-serif;
  /* Fluid: ~11px on phones → ~13px on desktop. Drops the discrete dppx /
     1024 / 768 overrides since the clamp covers the same range. */
  font-size: clamp(0.6875rem, 0.3vw + 0.55rem, 0.8125rem);
  font-weight: 400;
  line-height: 1.5;
`;

export const TabsSection = styled.div<{$isFullScreen: boolean}>`
  display: flex;
  flex-direction: column;
  gap: ${({$isFullScreen}) => ($isFullScreen ? '12px' : '12px')};
  min-width: 0;

  ${({$isFullScreen}) =>
    !$isFullScreen &&
    css`
      & .tabs-navigation,
      & [class*='tabs-navigation'] {
        border-bottom: none !important;
        overflow: visible !important;
        width: 100%;
        flex-direction: column !important;
        display: flex !important;
      }

      & .tabs-navigation > *,
      & [class*='tabs-navigation'] > * {
        display: flex !important;
        flex-direction: column !important;
        flex-wrap: nowrap !important;
        align-items: stretch !important;
        white-space: normal !important;
        row-gap: 2px !important;
        column-gap: 0 !important;
        width: 100% !important;
      }

      & .tab,
      & [class*='tab'],
      & .tabs-navigation button,
      & [class*='tabs-navigation'] button {
        flex: 0 0 auto !important;
        width: 100% !important;
        min-width: 0;
        font-family: 'Inter', sans-serif;
        /* Fluid side-panel tab font: ~11px → 13px. */
        font-size: clamp(0.6875rem, 0.3vw + 0.55rem, 0.8125rem);
        font-weight: 400;
        line-height: clamp(0.95rem, 0.4vw + 0.8rem, 1.125rem);
        color: #4b4b4b;
        padding: clamp(0.3rem, 0.7vh, 0.45rem) clamp(0.45rem, 0.9vw, 0.75rem) !important;
        white-space: normal !important;
        text-align: left !important;
        border-bottom: none !important;
        border-left: 2px solid transparent;
        height: auto !important;
      }

      & .tab.active,
      & .tab.is-active,
      & [class*='tab'].active,
      & .tabs-navigation button[aria-selected='true'],
      & [class*='tabs-navigation'] button[aria-selected='true'] {
        color: #027c06;
        font-weight: 700;
        border-bottom: none !important;
        border-left-color: #027c06 !important;
        background: rgba(2, 124, 6, 0.06);
      }
    `}

  @media (max-width: 1024px) {
    gap: 10px;
  }

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

export const TabContent = styled.div<{$isFullScreen: boolean}>`
  background: transparent;
  border-radius: 0;
  border: none;
  overflow-y: ${({$isFullScreen}) => ($isFullScreen ? 'auto' : 'visible')};
  overflow-x: hidden;
  /* dvh-relative cap when fullscreen so it scales with viewport instead of
     snapping at 280/380/420 breakpoints. Side-panel stays visible (no cap). */
  max-height: ${({$isFullScreen}) =>
    $isFullScreen ? 'clamp(18rem, 50dvh, 28rem)' : 'none'};

  @media (max-width: 768px) {
    border-radius: 6px;
  }
`;

export const InsightLoadingState = styled.div`
  width: 100%;
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const InsightErrorState = styled.p`
  margin: 0;
  color: #d40020;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 22px;
`;

export const MessagesSection = styled.section<{$isFullScreen?: boolean}>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  /* Side-panel is already narrow (parent container caps it), so 100% fills
     it naturally. Fullscreen expands to fill the viewport with a fluid
     clamp so large monitors actually use the available width. */
  max-width: ${({$isFullScreen}) =>
    $isFullScreen ? 'min(100%, clamp(40rem, 95vw, 90rem))' : '100%'};
  margin: 0 auto;

  @media (min-resolution: 1.44dppx) and (max-width: 1440px) and (min-width: 1024px) {
    .ai-chat-items .message-text,
    .ai-chat-items .message-footer,
    .ai-chat-items .message-slot,
    .ai-chat-items .message-slot-wrapper {
      font-size: 11px !important;
      line-height: 16px !important;
    }

    .ai-chat-items .bot-message-item,
    .ai-chat-items .user-message-item {
      gap: 10px;
    }
  }

  @media (max-width: 1024px) {
    gap: 12px;
  }

  @media (max-width: 768px) {
    gap: 10px;
  }
`;

export const RecentChatsList = styled.section<{$isFullScreen?: boolean}>`
  width: 100%;
  max-width: ${({$isFullScreen}) =>
    $isFullScreen ? 'min(100%, clamp(40rem, 95vw, 90rem))' : '100%'};
  margin: 0 auto;
  display: flex;
  flex-direction: column;
`;

export const RecentChatsRow = styled.button`
  width: 100%;
  border: none;
  border-bottom: 1px solid rgba(0, 90, 139, 0.14);
  background: transparent;
  text-align: left;
  padding: clamp(0.125rem, 0.2vh, 0.25rem) 0 clamp(0.25rem, 0.4vh, 0.375rem);
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.8125rem, 0.4vw + 0.7rem, 0.9375rem);
  font-weight: 400;
  line-height: 1.5;
  color: #4b4b4b;
  cursor: pointer;

  &:hover {
    color: ${peacock.primary};
  }
`;

export const SuggestionsSection = styled.section<{$isFullScreen?: boolean}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding-bottom: 0;
  background: transparent;
  width: 100%;
  max-width: min(100%, clamp(30rem, 50vw, 46rem));

  @media (min-resolution: 1.44dppx) and (max-width: 1440px) and (min-width: 1024px) {
    .tag,
    .tag .tag-label,
    .tag button {
      font-size: 11px !important;
      line-height: 16px !important;
    }
  }

  @media (max-width: 768px) {
    padding-bottom: 8px;
    gap: 6px;
  }
`;

export const SuggestionToggle = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin-top: clamp(0.125rem, 0.3vh, 0.3125rem);
  font-family: 'Inter', sans-serif;
  font-size: clamp(0.8125rem, 0.4vw + 0.7rem, 0.9375rem);
  font-weight: 700;
  line-height: 1.5;
  color: ${peacock.primary};
  cursor: pointer;
`;

export const FooterSection = styled.div<{$isFullScreen?: boolean}>`
  position: sticky;
  bottom: 0;
  z-index: 40;
  display: flex;
  flex-direction: column;
  /* Tight, fluid gap between composer and disclaimer. */
  gap: clamp(0.25rem, 0.6vh, 0.5rem);
  /* Responsive padding: hugs the footer on short viewports, breathes a bit
     on tall ones; horizontal scales with viewport width. */
  padding: 0 clamp(0.75rem, 1.5vw, 1.5rem) clamp(0.75rem, 1.6vh, 1.25rem);
  background: transparent;
  border-top: none;
  flex-shrink: 0;

  @media (min-resolution: 1.44dppx) and (max-width: 1440px) and (min-width: 1024px) {
    .disclaimer,
    .footer .field input,
    .footer .field textarea,
    .footer .field input::placeholder,
    .footer .field textarea::placeholder,
    .footer button,
    .footer [class*='label'] {
      font-size: 11px !important;
      line-height: 16px !important;
    }
  }

  @media (min-resolution: 1.44dppx) and (max-width: 1440px) {
    padding: 0 clamp(0.75rem, 1.2vw, 1rem) clamp(0.3rem, 0.8vh, 0.625rem);
    gap: clamp(0.2rem, 0.5vh, 0.4rem);
  }

  @media (max-width: 1024px) {
    padding: 0 clamp(0.75rem, 1.5vw, 1rem) clamp(0.3rem, 0.8vh, 0.625rem);
    gap: clamp(0.2rem, 0.5vh, 0.4rem);
  }

  @media (max-width: 768px) {
    padding: 0 0.75rem 0.5rem;
    gap: 0.25rem;
  }

  & > div {
    width: 100%;
  }

  & .footer-shell {
    width: 100%;
    max-width: ${({$isFullScreen}) =>
      $isFullScreen ? 'min(100%, clamp(30rem, 50vw, 60rem))' : '100%'};
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    gap: clamp(0.25rem, 0.6vh, 0.5rem);
    align-items: center;
  }

  & .footer-shell.is-loading textarea,
  & .footer-shell.is-loading input {
    pointer-events: none;
    opacity: 0.6;
    cursor: not-allowed;
    background: #f5f5f5;
  }

  /* Hide pepsico-ds send button slot while Stop is shown. */
  & .footer-shell.is-loading .footer .field button[aria-label*='Send' i],
  & .footer-shell.is-loading .footer .field [class*='send'] button {
    visibility: hidden;
  }

  & .footer {
    width: 100%;
    padding: 0;
    position: unset;
    max-width: ${({$isFullScreen}) =>
      $isFullScreen ? 'min(100%, clamp(30rem, 50vw, 60rem))' : '100%'};
  }

  & .footer::before {
    display: none;
  }

  /* A roomy, rounded card: input area on top, action row pinned to the
     bottom — the pepsico-ds .field is already column-flex by default, so
     we lean on that instead of fighting it into a single row. */
  & .footer .field,
  & .footer [class*='field'] {
    background: #ffffff;
    border: 1.5px solid rgba(0, 90, 139, 0.16);
    border-radius: 22px;
    box-shadow:
      0 14px 40px rgba(0, 59, 92, 0.1),
      0 2px 10px rgba(0, 59, 92, 0.06);
    min-width: stretch;
    max-width: 100% !important;
    width: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px;
    padding: clamp(1rem, 1.6vh, 1.375rem) clamp(1.125rem, 1.6vw, 1.5rem) !important;
    min-height: clamp(6.5rem, 14vh, 8rem);
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;
  }

  & .footer .field:hover,
  & .footer [class*='field']:hover {
    border-color: rgba(0, 90, 139, 0.3);
  }

  & .footer .field.typing,
  & .footer [class*='field'].typing {
    border-color: ${peacock.bright} !important;
    box-shadow:
      0 0 0 3px rgba(0, 160, 220, 0.14),
      0 14px 40px rgba(0, 59, 92, 0.1);
  }

  @media (max-width: 768px) {
    & .footer .field,
    & .footer [class*='field'] {
      border-radius: 18px;
      min-height: clamp(5rem, 16vh, 6rem);
    }
  }

  & .footer .inputContainer,
  & .footer [class*='inputContainer'] {
    width: 100% !important;
    max-width: 100% !important;
  }
  & .footer .field .inputArea,
  & .footer [class*='field'] [class*='inputArea'],
  & .footer .field [class*='inputArea'] {
    width: 100% !important;
    min-width: 0 !important;
    max-height: none !important;
    overflow: visible !important;
    align-items: flex-start !important;
    gap: 8px;
  }

  /* Decorative sparkle preceding the composer text, echoing the AI-assist
     framing of the greeting orb above. */
  & .footer .field .inputArea::before {
    content: '\\2726';
    flex-shrink: 0;
    margin-top: 1px;
    font-size: 1rem;
    line-height: 1.4;
    color: ${peacock.bright};
  }

  & .footer .field textarea {
    flex: 1 1 auto !important;
    /* Fill the .field row; field-sizing only controls height. */
    width: 100% !important;
    min-width: 0 !important;
    box-sizing: border-box !important;
    /* CSS-only auto-grow up to 4 lines, then scroll (ChatGPT/MUI style).
       Interpolated as a string so styled-components typings don't error. */
    ${'field-sizing: content !important;'}
    min-height: 1.4em !important;
    max-height: calc(1.4em * 4) !important;
    height: auto !important;
    line-height: 1.4 !important;
    padding-top: 2px !important;
    padding-bottom: 2px !important;
    resize: none !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;

    /* Small custom scrollbar — only visible once content exceeds 4 lines. */
    scrollbar-width: thin !important; /* Firefox */
    scrollbar-color: #c5c7cc transparent !important;

    &::-webkit-scrollbar {
      width: 4px !important;
      height: 4px !important;
    }
    &::-webkit-scrollbar-thumb {
      background: #c5c7cc !important;
      border-radius: 999px !important;
    }
    &::-webkit-scrollbar-thumb:hover {
      background: #a3a6ad !important;
    }
    &::-webkit-scrollbar-track {
      background: transparent !important;
      margin: 4px 0 !important;
    }
    &::-webkit-scrollbar-button {
      display: none !important;
      width: 0 !important;
      height: 0 !important;
    }
  }

  /* Hide the file-upload (paperclip) and any other left-side action
     controls in the composer. */
  & .footer .leftActions,
  & .footer [class*='leftActions'],
  & .footer [aria-label='Attach file'],
  & .footer [aria-label*='attach' i],
  & .footer [aria-label*='upload' i],
  & .footer button[title*='attach' i],
  & .footer button[title*='upload' i] {
    display: none !important;
  }

  /* Action row pinned to the bottom of the card, send button flush right. */
  & .footer .footerActions,
  & .footer [class*='footerActions'] {
    width: 100% !important;
  }

  & .footer .rightActions,
  & .footer [class*='rightActions'] {
    margin-left: auto !important;
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    flex: 0 0 auto !important;
    width: auto !important;
  }

  & .footer .rightActions [aria-label='Record audio'] {
    display: none;
  }

  /* The remaining rightActions control is Send — render it as a filled
     peacock-gradient circle, the one bright accent in the composer. */
  & .footer .rightActions button,
  & .footer .rightActions .base-button {
    width: 2.25rem !important;
    height: 2.25rem !important;
    min-width: 2.25rem !important;
    min-height: 2.25rem !important;
    padding: 0 !important;
    border-radius: 50% !important;
    border: none !important;
    background: ${peacockGradient} !important;
    box-shadow: 0 4px 14px rgba(0, 90, 139, 0.35);
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
    transition:
      transform 0.15s ease,
      box-shadow 0.15s ease;
  }

  & .footer .rightActions button:hover,
  & .footer .rightActions .base-button:hover {
    transform: scale(1.06);
    box-shadow: 0 6px 18px rgba(0, 90, 139, 0.4);
  }

  & .footer .rightActions button:disabled,
  & .footer .rightActions .base-button:disabled {
    opacity: 0.45;
    transform: none;
  }

  & .footer .rightActions button .icon,
  & .footer .rightActions button svg,
  & .footer .rightActions .base-button .icon,
  & .footer .rightActions .base-button svg {
    color: #ffffff !important;
    fill: #ffffff !important;
    width: 1.125rem !important;
    height: 1.125rem !important;
  }

  & .footer .disclaimer {
    display: none;
  }

  .rightActions .tooltip-parent {
    display: none;
  }

  /* The Stop-generating overlay (AiChatFooterHoc) is absolutely positioned
     against the whole footer; re-anchor it to the bottom-right corner of
     the now-taller card instead of the vertical center. */
  & div:has(> [aria-label='Stop generating response']) {
    top: auto !important;
    bottom: clamp(1.375rem, 2.2vh, 1.75rem) !important;
    right: clamp(1.5rem, 2vw, 1.875rem) !important;
    transform: none !important;
  }
`;

export const DisclaimerText = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  color: #8a97a3;
  margin: 0;
  text-align: center;
`;

export const ChatList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
`;

export const InsightButton = styled(Button)<{
  $selected?: boolean;
  $compact?: boolean;
}>`
  cursor: pointer;
  border-radius: 999px;
  min-height: ${({$compact}) => ($compact ? '28px' : '34px')};
  padding: ${({$compact}) => ($compact ? '0 10px' : '0 14px')};
  font-size: ${({$compact}) => ($compact ? '12px' : '13px')};
  line-height: 18px;
  font-weight: 700;
  width: fit-content;
  max-width: 100%;

  .label,
  .label-text,
  span {
    font-family: 'Inter', sans-serif;
    font-weight: 700;
  }

  @media (min-resolution: 1.44dppx) and (max-width: 1440px) {
    min-height: 26px;
    padding: 0 10px;
    font-size: 11px;
    line-height: 16px;
  }

  @media (max-width: 1024px) {
    min-height: 26px;
    padding: 0 10px;
    font-size: 12px;
    line-height: 16px;
  }

  @media (max-width: 768px) {
    min-height: 24px;
    padding: 0 8px;
    font-size: 11px;
    line-height: 16px;
  }

  ${({$selected}) =>
    $selected
      ? `
    background: #027c06 !important;
    color: #ffffff !important;
    border-color: #027c06 !important;
  `
      : `
    background: #f5f5f5 !important;
    color: #027c06 !important;
    border-color: #249f2f !important;
  `}

  &:hover {
    opacity: 0.92;
  }
`;

export const PromptTag = styled(Tag)`
  cursor: pointer;
  border-radius: 999px;
  width: fit-content;
  min-height: 34px;
  background: #ffffff;
  color: ${peacock.deepest};
  border: 1px solid rgba(0, 90, 139, 0.22);
  box-shadow: 0 1px 4px rgba(0, 59, 92, 0.05);

  .tag-label,
  .label,
  .label-text,
  span {
    color: ${peacock.deepest} !important;
  }

  @media (min-resolution: 1.44dppx) and (max-width: 1440px) {
    min-height: 30px;
    font-size: 12px;
    line-height: 18px;
  }

  @media (max-width: 1024px) {
    min-height: 30px;
    font-size: 13px;
  }

  @media (max-width: 768px) {
    width: 100%;
    min-height: 28px;
    font-size: 12px;
  }

  &:hover {
    background: rgba(0, 160, 220, 0.08);
    border-color: ${peacock.bright};
  }
`;
